/**
 * SystemErrorsTab — Platform Admin tab for centralised error monitoring.
 *
 * Features:
 *  - Metrics cards (total, by severity, open alerts)
 *  - Hourly volume sparkline
 *  - Top tenants with most errors
 *  - Top error routes
 *  - Paginated error list with filters
 *  - One-click resolve
 */
import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, RefreshCw, CheckCircle, Filter, X, TrendingUp, Server, Globe } from "lucide-react";

const SEV_COLOR = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high:     "bg-orange-100 text-orange-700 border-orange-200",
  medium:   "bg-amber-100 text-amber-700 border-amber-200",
  low:      "bg-gray-100 text-gray-500 border-gray-200",
};
const SEV_DOT = {
  critical: "bg-red-500",
  high:     "bg-orange-500",
  medium:   "bg-amber-400",
  low:      "bg-gray-400",
};

export function SystemErrorsTab({ adminAxios }) {
  const [metrics, setMetrics] = useState(null);
  const [errors, setErrors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;

  // Filters
  const [hours, setHours] = useState(24);
  const [filterRestaurant, setFilterRestaurant] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterDomain, setFilterDomain] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterRoute, setFilterRoute] = useState("");
  const [filterResolved, setFilterResolved] = useState(false);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const res = await adminAxios.get(`/error-logs/admin/metrics?hours=${hours}`);
      setMetrics(res.data);
    } catch { /* keep old data */ }
    setMetricsLoading(false);
  }, [adminAxios, hours]);

  const loadErrors = useCallback(async (newSkip = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ hours, skip: newSkip, limit: LIMIT });
      if (filterRestaurant) params.append("restaurant_id", filterRestaurant);
      if (filterSeverity) params.append("severity", filterSeverity);
      if (filterSource) params.append("source", filterSource);
      if (filterDomain) params.append("domain", filterDomain);
      if (filterStatus) params.append("status_code", filterStatus);
      if (filterRoute) params.append("route_contains", filterRoute);
      params.append("resolved", filterResolved ? "true" : "false");

      const res = await adminAxios.get(`/error-logs/admin/list?${params}`);
      setErrors(res.data?.errors || []);
      setTotal(res.data?.total || 0);
      setSkip(newSkip);
    } catch { /* keep old data */ }
    setLoading(false);
  }, [adminAxios, hours, filterRestaurant, filterSeverity, filterSource, filterDomain, filterStatus, filterRoute, filterResolved]);

  useEffect(() => { loadMetrics(); loadErrors(0); }, [loadMetrics, loadErrors]);

  const resolve = async (id) => {
    try {
      await adminAxios.patch(`/error-logs/admin/${id}/resolve`);
      setErrors(prev => prev.filter(e => e.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const resetFilters = () => {
    setFilterRestaurant(""); setFilterSeverity(""); setFilterSource("");
    setFilterDomain(""); setFilterStatus(""); setFilterRoute(""); setFilterResolved(false);
  };

  const sev = metrics?.by_severity || {};
  const maxHourly = Math.max(...(metrics?.hourly?.map(h => h.count) || [1]), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Erros do Sistema</h2>
          <p className="text-sm text-gray-500">Rastreamento centralizado por tenant, rota e severidade</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={hours} onChange={e => setHours(Number(e.target.value))}
            className="text-xs border rounded px-2 py-1.5 bg-white">
            <option value={1}>1 hora</option>
            <option value={6}>6 horas</option>
            <option value={24}>24 horas</option>
            <option value={72}>3 dias</option>
            <option value={168}>7 dias</option>
          </select>
          <button onClick={() => { loadMetrics(); loadErrors(0); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-xs font-medium transition">
            <RefreshCw className="w-3.5 h-3.5" /> Atualizar
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total", value: metrics?.total ?? "—", color: "text-gray-800", bg: "bg-gray-50" },
          { label: "Crítico", value: sev.critical ?? "—", color: "text-red-700", bg: "bg-red-50" },
          { label: "Alto", value: sev.high ?? "—", color: "text-orange-700", bg: "bg-orange-50" },
          { label: "Médio", value: sev.medium ?? "—", color: "text-amber-700", bg: "bg-amber-50" },
          { label: "Alertas abertos", value: metrics?.open_alerts ?? "—", color: "text-purple-700", bg: "bg-purple-50" },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-3 text-center`}>
            <div className={`text-2xl font-bold ${color}`}>{metricsLoading ? "…" : value}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Hourly chart + domain/route breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Hourly sparkline */}
        <div className="md:col-span-2 bg-white border rounded-xl p-4">
          <p className="text-xs font-bold text-gray-600 mb-3 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Volume por hora
          </p>
          {metricsLoading ? (
            <div className="h-16 bg-gray-100 animate-pulse rounded" />
          ) : (
            <div className="flex items-end gap-0.5 h-16">
              {(metrics?.hourly || []).map((h, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${h.hour}: ${h.count}`}>
                  <div
                    className="w-full bg-emerald-500 rounded-t opacity-80 hover:opacity-100 transition"
                    style={{ height: `${Math.round((h.count / maxHourly) * 100)}%`, minHeight: h.count ? 2 : 0 }}
                  />
                </div>
              ))}
              {(metrics?.hourly || []).length === 0 && (
                <div className="w-full text-center text-xs text-gray-400 py-4">Sem dados</div>
              )}
            </div>
          )}
        </div>

        {/* Top domains */}
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs font-bold text-gray-600 mb-3 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" /> Por domínio
          </p>
          <div className="space-y-1.5">
            {metricsLoading ? (
              [1,2,3].map(i => <div key={i} className="h-5 bg-gray-100 animate-pulse rounded" />)
            ) : (metrics?.by_domain || []).slice(0, 6).map(d => (
              <div key={d.domain} className="flex items-center justify-between text-xs">
                <span className="font-medium text-gray-700 capitalize">{d.domain}</span>
                <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded-full">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top tenants + top routes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs font-bold text-gray-600 mb-3">Top tenants com mais erros</p>
          <div className="space-y-1.5">
            {metricsLoading ? [1,2,3].map(i => <div key={i} className="h-5 bg-gray-100 animate-pulse rounded" />) :
              (metrics?.top_tenants || []).slice(0, 8).map(t => (
                <div key={t.restaurant_id} className="flex items-center justify-between text-xs">
                  <span className="text-gray-700 font-medium truncate max-w-[60%]" title={t.restaurant_name}>{t.restaurant_name || t.restaurant_id}</span>
                  <div className="flex items-center gap-1.5">
                    {t.critical > 0 && <span className="text-red-600 font-bold text-[10px]">{t.critical}🔴</span>}
                    <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded-full">{t.count}</span>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs font-bold text-gray-600 mb-3">Top rotas com erros</p>
          <div className="space-y-1.5">
            {metricsLoading ? [1,2,3].map(i => <div key={i} className="h-5 bg-gray-100 animate-pulse rounded" />) :
              (metrics?.top_routes || []).slice(0, 8).map(r => (
                <div key={r.route} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 font-mono truncate max-w-[60%]" title={r.route}>{r.route}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500 text-[10px]">{r.status_code}</span>
                    <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded-full">{r.count}</span>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-xs font-bold text-gray-600">Filtros</span>
          <button onClick={resetFilters} className="ml-auto text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-1">
            <X className="w-3 h-3" /> Limpar
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <input value={filterRestaurant} onChange={e => setFilterRestaurant(e.target.value)}
            placeholder="Restaurant ID" className="text-xs border rounded px-2 py-1.5 col-span-2" />
          <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
            className="text-xs border rounded px-2 py-1.5">
            <option value="">Severidade</option>
            <option value="critical">Crítico</option>
            <option value="high">Alto</option>
            <option value="medium">Médio</option>
            <option value="low">Baixo</option>
          </select>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
            className="text-xs border rounded px-2 py-1.5">
            <option value="">Origem</option>
            <option value="frontend">Frontend</option>
            <option value="backend">Backend</option>
          </select>
          <input value={filterRoute} onChange={e => setFilterRoute(e.target.value)}
            placeholder="Rota (contém)" className="text-xs border rounded px-2 py-1.5" />
          <input value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            placeholder="Status HTTP" type="number" className="text-xs border rounded px-2 py-1.5" />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={filterResolved} onChange={e => setFilterResolved(e.target.checked)}
              className="rounded" />
            Mostrar resolvidos
          </label>
          <button onClick={() => loadErrors(0)}
            className="ml-auto px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded transition">
            Aplicar filtros
          </button>
        </div>
      </div>

      {/* Error list */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
            <Server className="w-3.5 h-3.5" /> Erros recentes ({total} total)
          </span>
          <div className="flex items-center gap-2">
            <button disabled={skip === 0} onClick={() => loadErrors(Math.max(0, skip - LIMIT))}
              className="text-xs px-2 py-1 border rounded disabled:opacity-40 hover:bg-gray-100 transition">← Anterior</button>
            <span className="text-xs text-gray-500">{skip + 1}–{Math.min(skip + LIMIT, total)}</span>
            <button disabled={skip + LIMIT >= total} onClick={() => loadErrors(skip + LIMIT)}
              className="text-xs px-2 py-1 border rounded disabled:opacity-40 hover:bg-gray-100 transition">Próximo →</button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 animate-pulse rounded" />)}
          </div>
        ) : errors.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nenhum erro encontrado</p>
          </div>
        ) : (
          <div className="divide-y">
            {errors.map(err => (
              <div key={err.id} className="px-4 py-3 hover:bg-gray-50 flex items-start gap-3">
                {/* Severity dot */}
                <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${SEV_DOT[err.severity] || "bg-gray-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${SEV_COLOR[err.severity] || SEV_COLOR.low}`}>
                      {err.severity?.toUpperCase()}
                    </span>
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{err.source}</span>
                    <span className="text-[10px] text-gray-500 font-mono">{err.method} {err.route}</span>
                    {err.status_code > 0 && (
                      <span className={`text-[10px] font-bold ${err.status_code >= 500 ? "text-red-600" : "text-amber-600"}`}>
                        HTTP {err.status_code}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 mt-1 truncate">{err.message}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                    <span>{err.created_at?.slice(0, 19).replace("T", " ")} UTC</span>
                    {err.restaurant_id && <span className="font-mono">{err.restaurant_id.slice(0, 8)}…</span>}
                    {err.user_email && <span>{err.user_email}</span>}
                    {err.domain && <span className="capitalize bg-gray-100 px-1.5 py-0.5 rounded">{err.domain}</span>}
                  </div>
                </div>
                {!err.resolved && (
                  <button onClick={() => resolve(err.id)}
                    className="flex-shrink-0 text-[10px] text-emerald-600 hover:text-emerald-800 border border-emerald-200 hover:border-emerald-400 px-2 py-1 rounded transition"
                    title="Marcar como resolvido">
                    Resolver
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
